// Shared page-layout engine for all half-form (9.5" x 5.5") ESC/P documents.
//
// Every continuous-paper document (SPB, SPG, SPK, STB, Retur, Surat Jalan) has
// the same skeleton as its HTML twin: centered title with an underline, a page
// number, a two-column info block, a fully BOXED table (borders drawn with
// CP437 box characters, like the HTML table borders), a per-page TOTAL row with
// the label merged across the left columns (the HTML colspan), and a footer
// with notes on the left and the signature on the right (the HTML footer flex).
// Each document builder only supplies a config; this module renders the pages.
//
// Requires the printer char table PC437 (CHARTABLE_PC437) and the QZ config
// encoding "CP437" so the box characters reach the printer intact.

import { formatNumberWithDot } from "../numberUtils";
import {
  INIT,
  LQ_ON,
  TYPEFACE_ROMAN,
  ELITE,
  PICA,
  CONDENSED_ON,
  CONDENSED_OFF,
  BOLD_ON,
  BOLD_OFF,
  DOUBLE_STRIKE_ON,
  DOUBLE_STRIKE_OFF,
  DOUBLE_WIDTH_ON,
  DOUBLE_WIDTH_OFF,
  CHARTABLE_PC437,
  leftMargin,
  LINE_SPACING_1_6,
  lineSpacing216,
  formLengthLines,
  pad,
  wrapText,
  LF,
  FF,
} from "./escpBuilder";

// 5.5 inch x 6 LPI = 33 lines per page. FF jumps to the next 5.5" form top,
// keeping content aligned with the paper perforation.
const HALF_PAGE_LINES = 33;
// Body font: condensed pica (~17.1 cpi, ~8pt). The title stays at elite
// (12 cpi, ~10pt) — see the title block in buildPage(). At 17.1 cpi the
// 94-column table is ~5.5" wide, well inside the 8" line.
// Note: the LX-310's NLQ fonts only support 10/12 cpi, so condensed body text
// prints in the (sharp but lighter) draft font. If it looks too light on
// paper, DOUBLE_STRIKE can be added to darken it.
const LEFT_MARGIN_COLS = 1; // ~1.5 mm at condensed pitch (printer minimum step)

// Physical width ratio between a body column (17.1 cpi) and a title column
// (12 cpi) — used to center the elite title over the condensed table.
const TITLE_SCALE = 12 / 17.14;

// CP437 box-drawing characters (encoded by QZ with encoding "CP437").
const H = "─"; // ─
const V = "│"; // │
const TL = "┌"; // ┌
const TR = "┐"; // ┐
const BL = "└"; // └
const BR = "┘"; // ┘
const TT = "┬"; // ┬
const BT = "┴"; // ┴
const LT = "├"; // ├
const RT = "┤"; // ┤
const XX = "┼"; // ┼

/**
 * Horizontal border line. `joints` is an array with one character per internal
 * column boundary (length = widths.length - 1).
 */
const hLine = (widths, left, joints, right) => {
  let out = left;
  widths.forEach((w, i) => {
    out += H.repeat(w);
    if (i < widths.length - 1) out += joints[i];
  });
  return out + right;
};

/** One boxed content row. Bold wraps only the cell text, not the borders. */
const boxRow = (cells, bold = false) =>
  V +
  cells
    .map((c) => {
      const text = pad(c.text, c.width, c.align);
      return bold ? BOLD_ON + text + BOLD_OFF : text;
    })
    .join(V) +
  V;

/** Width of the first `span` columns merged into one cell (incl. separators). */
const mergedWidth = (widths, span) =>
  widths.slice(0, span).reduce((sum, w) => sum + w, 0) + (span - 1);

const buildPage = (config, data, pageItems, pageIndex, totalPages) => {
  const widths = config.columns.map((c) => c.width);
  const tableWidth = widths.reduce((sum, w) => sum + w, 0) + widths.length + 1;
  let out = "";
  // No top margin: content starts on the form's first line, right below the
  // perforation.

  // Title — bold + double-strike, centered; underlined like the HTML header.
  // Prints at elite (12 cpi) while the body is condensed, so the centering pad
  // (emitted at elite too) is computed against the table's PHYSICAL width.
  const charW = config.titleDoubleWidth ? 2 : 1;
  const titlePad = Math.max(
    0,
    Math.floor((tableWidth * TITLE_SCALE - config.title.length * charW) / 2)
  );
  out +=
    CONDENSED_OFF + ELITE +
    " ".repeat(titlePad) +
    (config.titleDoubleWidth ? DOUBLE_WIDTH_ON : "") +
    BOLD_ON + DOUBLE_STRIKE_ON +
    config.title +
    DOUBLE_STRIKE_OFF + BOLD_OFF +
    (config.titleDoubleWidth ? DOUBLE_WIDTH_OFF : "") +
    PICA + CONDENSED_ON +
    LF;

  // Page number — same rule as the HTML: only shown on multi-page documents.
  if (totalPages > 1) {
    out += pad(`Halaman ${pageIndex + 1} dari ${totalPages}`, tableWidth, "center") + LF;
  }
  // ~10px (2.6 mm) gap before the underline — the HTML .header padding-bottom —
  // via one fine 23/216" feed, then restore 1/6" line spacing.
  out += lineSpacing216(23) + LF + LINE_SPACING_1_6;
  out += H.repeat(tableWidth) + LF + LF;

  // Document info (two columns), mirroring the HTML info block.
  config.infoLines(data).forEach(([lLabel, lValue, rLabel, rValue]) => {
    const labelW = config.infoLabelWidth || 13;
    const leftBlock = lLabel ? pad(lLabel, labelW) + ": " + (lValue ?? "-") : "";
    const rightBlock = rLabel ? pad(rLabel, labelW) + ": " + (rValue ?? "-") : "";
    out += pad(leftBlock, 38) + rightBlock + LF;
  });
  out += LF;

  // --- Boxed table ------------------------------------------------------------
  const n = widths.length;
  const group = config.groupHeader; // e.g. { label: "JUMLAH", span: 2 }

  if (group) {
    // Top border + group row: the left columns form one merged blank cell, the
    // last `span` columns sit under a centered group label (HTML colspan row).
    const leftSpan = n - group.span;
    const leftW = mergedWidth(widths, leftSpan);
    const groupW = mergedWidth(widths.slice(leftSpan), group.span);
    out += TL + H.repeat(leftW) + TT + H.repeat(groupW) + TR + LF;
    out +=
      V + pad("", leftW) + V + BOLD_ON + pad(group.label, groupW, "center") + BOLD_OFF + V + LF;
    // Separator that opens the individual columns below.
    out +=
      hLine(
        widths,
        LT,
        widths.slice(0, -1).map((w, i) => (i === leftSpan - 1 ? XX : TT)),
        RT
      ) + LF;
  } else {
    out += hLine(widths, TL, widths.slice(0, -1).map(() => TT), TR) + LF;
  }

  // Header row + separator.
  out += boxRow(config.columns.map((c) => ({ text: c.head, width: c.width, align: "center" })), true) + LF;
  out += hLine(widths, LT, widths.slice(0, -1).map(() => XX), RT) + LF;

  // Item rows.
  pageItems.forEach((item) => {
    out += boxRow(config.columns.map((c) => ({ text: c.value(item), width: c.width, align: c.align }))) + LF;
  });

  // TOTAL row: label merged across the left columns (HTML colspan), then one
  // cell per remaining column.
  const span = config.totalSpan;
  const totals = config.totals(pageItems);
  const totalJoints = widths.slice(0, -1).map((w, i) => (i < span - 1 ? BT : XX));
  out += hLine(widths, LT, totalJoints, RT) + LF;
  out +=
    V + BOLD_ON + pad("TOTAL ", mergedWidth(widths, span), "right") + BOLD_OFF +
    totals
      .map((t, i) => V + BOLD_ON + pad(t, widths[span + i], "right") + BOLD_OFF)
      .join("") +
    V + LF;
  const bottomJoints = widths.slice(0, -1).map((w, i) => (i < span - 1 ? H : BT));
  out += hLine(widths, BL, bottomJoints, BR) + LF;
  out += LF;

  // --- Footer: notes left, signature right (HTML footer flex) ------------------
  const sigName = config.signatureName(data);
  if (config.notes) {
    const noteLines = wrapText(config.notes(data), tableWidth - 24, 3);
    // BOLD codes must stay outside pad() — pad/clean strips control characters.
    out +=
      BOLD_ON + "CATATAN :" + BOLD_OFF +
      pad("", tableWidth - 24 - "CATATAN :".length) +
      pad(config.signatureLabel, 24, "right") + LF;
    out += pad(noteLines[0] ?? "", tableWidth) + LF;
    out += pad(noteLines[1] ?? "", tableWidth) + LF;
    out += pad(noteLines[2] ?? "", tableWidth - 24) + pad(sigName, 24, "right") + LF;
  } else {
    out += pad(config.signatureLabel, tableWidth, "right") + LF + LF + LF;
    out += pad(sigName, tableWidth, "right") + LF;
  }

  return out;
};

/**
 * Build a complete half-form ESC/P document from a document config.
 * @param {object} config  title, infoLines(data), columns[], groupHeader,
 *                         totalSpan, totals(pageItems), notes(data),
 *                         signatureLabel, signatureName(data), maxItemsPerPage
 * @param {object} data    the same data object the HTML printer receives
 * @param {number} itemsPerPage rows per page (clamped to config.maxItemsPerPage)
 * @returns {string} ESC/P bytes ready for qzClient.printRaw()
 */
export const buildHalfFormDoc = (config, data, itemsPerPage) => {
  const items = data.items || [];
  const perPage = Math.min(Math.max(1, itemsPerPage), config.maxItemsPerPage);
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));

  // 6 LPI then a 5.5" form length so each page = one physical half-form; PC437
  // char table so the box-drawing borders print correctly.
  let doc =
    INIT +
    LQ_ON +
    TYPEFACE_ROMAN +
    CHARTABLE_PC437 +
    PICA +
    CONDENSED_ON +
    LINE_SPACING_1_6 +
    formLengthLines(HALF_PAGE_LINES) +
    leftMargin(LEFT_MARGIN_COLS);

  for (let page = 0; page < totalPages; page++) {
    const pageItems = items.slice(page * perPage, (page + 1) * perPage);
    doc += buildPage(config, data, pageItems, page, totalPages);
    if (page < totalPages - 1) doc += FF; // next page top-of-form
  }

  doc += FF; // eject final page
  return doc;
};

// The 7-column table shared by SPB / SPG / SPK / STB / Retur — identical in all
// their HTML versions. Widths follow the HTML pixel proportions (120 / 65 / 200 /
// 60 / 60 / 70 / 70 px) scaled to 68 content columns (printed at 12-cpi elite).
export const STANDARD_COLUMNS = [
  { head: "KODE PRODUK", width: 13, align: "left", value: (i) => i.product_code || "-" },
  { head: "BARCODE", width: 7, align: "left", value: () => "-" },
  { head: "NAMA PRODUK", width: 39, align: "left", value: (i) => i.product_name || "-" },
  { head: "KP", width: 6, align: "left", value: (i) => i.supplier_name || "-" },
  { head: "PACKING", width: 7, align: "left", value: (i) => i.packing || "-" },
  { head: "CARTON", width: 7, align: "right", value: (i) => formatNumberWithDot(i.carton_quantity || 0) },
  { head: "PACK", width: 7, align: "right", value: (i) => formatNumberWithDot(i.pack_quantity || 0) },
];

/** Per-page CARTON/PACK totals for STANDARD_COLUMNS documents. */
export const STANDARD_TOTALS = (pageItems) => {
  let carton = 0;
  let pack = 0;
  pageItems.forEach((i) => {
    carton += i.carton_quantity || 0;
    pack += i.pack_quantity || 0;
  });
  return [formatNumberWithDot(carton), formatNumberWithDot(pack)];
};

export default buildHalfFormDoc;
